import { useCallback, useEffect, useRef, useState } from 'react';
import { myDeliveries, changeDeliveryStatus, reorderDeliveries, updateEstimateArrived, opsErr, okCode } from '../../api/ops';
import { useToast } from '../../components/Toast';
import { useModal } from '../../components/ModalProvider';
import { OpsGate, useOps, ScanSearch, DateRange, PhotoPicker, ImgGrid, imgUrl, daysAgo, toInputDate, startOfInput, endOfInput, localIso, fmtMoney } from './opsCommon';

// GIAO HÀNG (app "i love GTS") — 1 màn, 3 chế độ theo delivery_status:
//  WAITING = Nhận hàng đi giao (xác nhận số kiện -> PENDING)
//  PENDING = Giao hàng (Thành công có ảnh / Thất bại có lý do / Cập nhật giờ dự kiến / sắp thứ tự)
//  SUCCESS = Quản lý đơn đã giao (xem chi tiết + ảnh giao)
const MODES = {
  WAITING: { title: 'Nhận hàng đi giao', sub: 'Quét mã đơn để xác nhận nhận hàng đi giao' },
  PENDING: { title: 'Giao hàng', sub: 'Đơn đang giao của tôi — cập nhật kết quả giao' },
  SUCCESS: { title: 'Quản lý đơn giao', sub: 'Đơn đã giao thành công' },
};
const ETA = [['15 phút', 15], ['30 phút', 30], ['1 tiếng', 60], ['1 tiếng 30 phút', 90], ['2 tiếng', 120]];
const FAIL_REASONS = ['Khách hàng không nhận hàng', 'Không liên lạc được với khách', 'Khách hàng đổi địa chỉ nhận hàng', 'Lý do khác'];

const ship = (it) => (it.shipments && it.shipments[0]) || {};
const fullAddr = (s) => [s.consignee_address, s.consignee_ward, s.consignee_district, s.consignee_province].filter(Boolean).join(', ');

function Busy({ busy, children, ...p }) {
  return <button {...p} disabled={busy || p.disabled}>{busy ? 'Đang gửi…' : children}</button>;
}

// Nhận hàng đi giao: nhập số kiện thực nhận.
function ConfirmPickup({ item, onDone, onClose }) {
  const s = ship(item);
  const [n, setN] = useState(String(s.num_pieces ?? ''));
  const [busy, setBusy] = useState(false);
  const valid = /^\d+$/.test(n) && Number(n) > 0;
  return (
    <div>
      <h3>XÁC NHẬN GIAO HÀNG</h3>
      <div className="ops-kv" style={{ marginBottom: 12 }}>
        <div><span>Bảng kê</span><b>{item.batch_code}</b></div>
        <div><span>Mã đơn</span><b>{item.shipment_code}</b></div>
        <div><span>Người nhận</span><b>{s.consignee_name}</b></div>
        <div><span>Điện thoại</span><b>{s.consignee_phone}</b></div>
        <div><span>Địa chỉ</span><b>{fullAddr(s)}</b></div>
      </div>
      <div className="field">
        <label className="small">Số kiện</label>
        <input type="number" inputMode="numeric" value={n} onChange={(e) => setN(e.target.value)} />
        {!valid && <div className="small" style={{ color: 'var(--danger)' }}>Số kiện phải là số nguyên dương</div>}
      </div>
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button className="btn ghost" onClick={onClose}>Hủy</button>
        <Busy className="btn" busy={busy} disabled={!valid} onClick={async () => { setBusy(true); await onDone({ delivery_status: 'PENDING', num_pieces: Number(n) }); setBusy(false); }}>Phát hàng</Busy>
      </div>
    </div>
  );
}

function FailForm({ item, onDone, onClose }) {
  const [reason, setReason] = useState('');
  const [other, setOther] = useState('');
  const [busy, setBusy] = useState(false);
  const note = reason === 'Lý do khác' ? other.trim() : reason;
  return (
    <div>
      <h3>Giao không thành công</h3>
      <div className="small muted" style={{ marginBottom: 10 }}>{item.shipment_code} · {fmtMoney(ship(item).total)} đ</div>
      <div className="ops-chips">
        {FAIL_REASONS.map((r) => (
          <button key={r} type="button" className={`chip${reason === r ? ' done' : ' todo'}`} onClick={() => setReason(r)}>{r}</button>
        ))}
      </div>
      {reason === 'Lý do khác' && (
        <textarea rows={3} placeholder="Nhập lý do…" value={other} onChange={(e) => setOther(e.target.value)} style={{ marginTop: 10 }} />
      )}
      <div className="row" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
        <button className="btn ghost" onClick={onClose}>Hủy</button>
        <Busy className="btn danger" busy={busy} disabled={!note} onClick={async () => { setBusy(true); await onDone({ delivery_status: 'CANCEL', delivery_note: note }); setBusy(false); }}>Xác nhận</Busy>
      </div>
    </div>
  );
}

function SuccessForm({ item, onDone, onClose }) {
  const [note, setNote] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  return (
    <div>
      <h3>Giao thành công</h3>
      <div className="small muted" style={{ marginBottom: 10 }}>{item.shipment_code} · {fmtMoney(ship(item).total)} đ</div>
      <div className="field">
        <label className="small">Ghi chú</label>
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <div className="field">
        <label className="small">Hình ảnh <span style={{ color: 'var(--danger)' }}>*</span></label>
        <PhotoPicker files={files} setFiles={setFiles} />
      </div>
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button className="btn ghost" onClick={onClose}>Hủy</button>
        <Busy className="btn ok" busy={busy} disabled={!files.length} onClick={async () => { setBusy(true); await onDone({ delivery_status: 'SUCCESS', delivery_note: note.trim(), files: files.map((f) => f.file) }); setBusy(false); }}>Xác nhận</Busy>
      </div>
    </div>
  );
}

function EtaForm({ item, onClose }) {
  const toast = useToast();
  const [min, setMin] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const d = await updateEstimateArrived(item.shipment_code, localIso(new Date(Date.now() + min * 60000)));
      if (okCode(d)) { toast('Cập nhật thời gian giao dự kiến thành công'); onClose(); }
      else toast(d?.message || 'Cập nhật thời gian giao dự kiến không thành công', 'danger');
    } catch (e) {
      toast(opsErr(e, 'Cập nhật thời gian giao dự kiến không thành công'), 'danger');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <h3>Chọn thời gian dự kiến giao tới</h3>
      <div className="ops-chips">
        {ETA.map(([label, m]) => (
          <button key={m} type="button" className={`chip${min === m ? ' done' : ' todo'}`} onClick={() => setMin(m)}>{label}</button>
        ))}
      </div>
      <div className="row" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
        <button className="btn ghost" onClick={onClose}>Hủy</button>
        <Busy className="btn" busy={busy} disabled={!min} onClick={submit}>Xác nhận</Busy>
      </div>
    </div>
  );
}

function Detail({ item, base }) {
  const s = ship(item);
  return (
    <div>
      <h3>Chi tiết đơn giao</h3>
      <div className="ops-kv">
        <div><span>Mã đơn</span><b>{item.shipment_code}</b></div>
        <div><span>Người nhận</span><b>{s.consignee_name}</b></div>
        <div><span>Địa chỉ</span><b>{fullAddr(s)}</b></div>
        <div><span>Điện thoại</span><b>{s.consignee_phone}</b></div>
        <div><span>Số kiện</span><b>{s.num_pieces ?? 0}</b></div>
        <div><span>Tổng tiền</span><b>{fmtMoney(s.total)} đ</b></div>
        {item.delivery_note && <div><span>Ghi chú</span><b>{item.delivery_note}</b></div>}
      </div>
      {item.image ? <div style={{ marginTop: 12 }}><ImgGrid urls={[imgUrl(base, item.image)]} /></div> : <div className="small muted" style={{ marginTop: 12 }}>Không có ảnh giao hàng.</div>}
    </div>
  );
}

function Deliveries({ mode }) {
  const account = useOps();
  const toast = useToast();
  const { openModal, closeModal } = useModal();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [from, setFrom] = useState(toInputDate(daysAgo(7)));
  const [to, setTo] = useState(toInputDate(new Date()));
  const seq = useRef(0);

  const load = useCallback(async (search = '') => {
    const my = ++seq.current;
    setLoading(true);
    try {
      const d = await myDeliveries({
        from: localIso(startOfInput(from)), to: localIso(endOfInput(to)), search,
        staff_id: account.sxUserId, delivery_type: 'DR', delivery_status: mode, size: 50,
      });
      const rows = okCode(d) ? d.data?.results || [] : [];
      if (my === seq.current) setList(rows);
      return rows;
    } catch (e) {
      if (my === seq.current) { setList([]); toast(opsErr(e, 'Không tải được danh sách đơn'), 'danger'); }
      return [];
    } finally {
      if (my === seq.current) setLoading(false);
    }
  }, [account.sxUserId, from, to, mode, toast]);

  useEffect(() => { load(q); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  // Gửi đổi trạng thái giao (multipart) rồi tải lại.
  const change = (item) => async (payload) => {
    try {
      const d = await changeDeliveryStatus({ shipment_code: item.shipment_code, batch_code: item.batch_code, ...payload });
      if (okCode(d)) { toast('Cập nhật thành công'); closeModal(); load(q); }
      else toast(d?.message || 'Cập nhật thất bại', 'danger');
    } catch (e) {
      toast(opsErr(e, 'Cập nhật thất bại'), 'danger');
    }
  };
  const open = (Comp, item) => openModal(<Comp item={item} base={account.base} onDone={change(item)} onClose={closeModal} />);

  const onSearch = async (code, opts = {}) => {
    const rows = await load(code);
    // Quét mã ở màn Nhận hàng đi giao -> ra đúng 1 đơn thì mở luôn hộp xác nhận.
    if (mode === 'WAITING' && opts.scanned && rows.length === 1) open(ConfirmPickup, rows[0]);
  };

  // Sắp thứ tự giao: đổi chỗ 2 đơn, giữ nguyên tập index cũ theo vị trí (như app gốc) rồi lưu.
  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const idx = list.map((x) => x.index);
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    const sorted = next.map((x, k) => ({ ...x, index: idx[k] }));
    setList(sorted);
    try {
      const d = await reorderDeliveries(sorted.map((x) => ({ code: x.shipment_code, index: x.index ?? 0 })));
      if (!okCode(d)) toast(d?.message || 'Cập nhật thứ tự thất bại', 'danger');
    } catch (e) {
      toast(opsErr(e, 'Cập nhật thứ tự thất bại'), 'danger');
    }
  };

  return (
    <>
      <ScanSearch value={q} onChange={setQ} onSubmit={onSearch} placeholder="Tìm / quét mã đơn…" busy={loading} />
      <div className="row between" style={{ marginBottom: 10 }}>
        <span className="small">Tổng: <b>{list.length}</b> đơn hàng</span>
        <DateRange from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} />
      </div>
      {mode === 'PENDING' && list.length > 1 && <div className="small muted" style={{ marginBottom: 8 }}>* Dùng mũi tên ▲▼ để đổi thứ tự giao</div>}
      {loading ? <div className="empty">Đang tải…</div> : !list.length ? <div className="empty">Không có đơn.</div> : (
        <div className="ops-list">
          {list.map((it, i) => {
            const s = ship(it);
            return (
              <div key={it._id || it.shipment_code} className={`card ops-item${mode === 'SUCCESS' ? ' ops-picked' : ''}`}>
                <div className="row between">
                  <b>{it.shipment_code}</b>
                  <span className="row" style={{ gap: 6 }}>
                    <b>{fmtMoney(s.total ?? 0)} đ</b>
                    {mode === 'PENDING' && (
                      <span className="ops-order">
                        <button type="button" disabled={i === 0} onClick={() => move(i, -1)} title="Lên">▲</button>
                        <button type="button" disabled={i === list.length - 1} onClick={() => move(i, 1)} title="Xuống">▼</button>
                      </span>
                    )}
                  </span>
                </div>
                <div className="small">{s.consignee_name}{s.consignee_phone ? <> · <a href={`tel:${s.consignee_phone}`}>{s.consignee_phone}</a></> : ''}</div>
                {mode !== 'SUCCESS' && <div className="small muted">{fullAddr(s)}</div>}
                <div className="small muted">
                  {s.num_pieces ?? 0} kiện
                  {mode !== 'SUCCESS' && Number(it.delivery_failed) > 0 && <b style={{ color: 'var(--danger)' }}> · Giao thất bại: {it.delivery_failed}</b>}
                  {it.batch_code ? ` · Bảng kê ${it.batch_code}` : ''}
                </div>
                <div className="row" style={{ marginTop: 8 }}>
                  {mode === 'WAITING' && <button className="btn sm" onClick={() => open(ConfirmPickup, it)}>Phát hàng</button>}
                  {mode === 'PENDING' && (
                    <>
                      <button className="btn sm danger" onClick={() => open(FailForm, it)}>Thất bại</button>
                      <button className="btn sm ghost" onClick={() => open(EtaForm, it)}>Cập nhật TG</button>
                      <button className="btn sm ok" onClick={() => open(SuccessForm, it)}>Thành công</button>
                    </>
                  )}
                  {mode === 'SUCCESS' && <button className="btn sm ghost" onClick={() => open(Detail, it)}>Xem chi tiết</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

export default function OpsDeliveryPage({ mode, onBack }) {
  const m = MODES[mode];
  return (
    <OpsGate onBack={onBack} title={m.title} sub={m.sub}>
      <Deliveries key={mode} mode={mode} />
    </OpsGate>
  );
}
